import { useQuery, useMutation } from "@tanstack/react-query";
import type { UseQueryOptions, UseMutationOptions } from "@tanstack/react-query";
import { customFetch } from "./custom-fetch";
import type { ErrorType } from "./custom-fetch";

export interface ConnectStatus {
  connected: boolean;
  status: "active" | "pending" | "disabled" | null;
  accountId: string | null;
  chargesEnabled?: boolean;
  detailsSubmitted?: boolean;
}

export const CONNECT_STATUS_QUERY_KEY = ["/api/billing/connect/status"] as const;

export const getConnectStatus = async (): Promise<ConnectStatus> =>
  customFetch<ConnectStatus>("/api/billing/connect/status");

export const useGetConnectStatus = <
  TData = ConnectStatus,
  TError = ErrorType<unknown>
>(options?: {
  query?: UseQueryOptions<ConnectStatus, TError, TData>;
}) => {
  const { query: queryOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? CONNECT_STATUS_QUERY_KEY;

  return useQuery<ConnectStatus, TError, TData>({
    queryKey,
    queryFn: getConnectStatus,
    ...queryOptions,
  });
};

export const startConnectOnboarding = async (): Promise<{ url: string }> =>
  customFetch<{ url: string }>("/api/billing/connect/onboard", { method: "POST" });

export const useStartConnectOnboarding = <
  TError = ErrorType<unknown>,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<{ url: string }, TError, void, TContext>;
}) => {
  return useMutation<{ url: string }, TError, void, TContext>({
    mutationFn: startConnectOnboarding,
    ...options?.mutation,
  });
};

export const getConnectDashboardLink = async (): Promise<{ url: string }> =>
  customFetch<{ url: string }>("/api/billing/connect/dashboard-link", { method: "POST" });

export const useGetConnectDashboardLink = <
  TError = ErrorType<unknown>,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<{ url: string }, TError, void, TContext>;
}) => {
  return useMutation<{ url: string }, TError, void, TContext>({
    mutationFn: getConnectDashboardLink,
    ...options?.mutation,
  });
};

export const generateInvoicePaymentLink = async (invoiceId: number): Promise<{ url: string }> =>
  customFetch<{ url: string }>(`/api/invoices/${invoiceId}/payment-link`, { method: "POST" });

export const useGenerateInvoicePaymentLink = <
  TError = ErrorType<unknown>,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<{ url: string }, TError, number, TContext>;
}) => {
  return useMutation<{ url: string }, TError, number, TContext>({
    mutationFn: generateInvoicePaymentLink,
    ...options?.mutation,
  });
};
